import { logApiError } from '../services/errorReporting';
import { getLocaleFromCookie } from '../components/lang/LocalizationProvider';
import { saveTokens, isLoggedIn, getAccessToken, getRefreshToken, removeTokens } from './auth';

const baseUrl = process.env.API_BASE_URL;

const appIdentity = 'my.clearvpn.com';

/**
 * Makes a `fetch` call
 * @param {string} url
 * @param {string} method
 * @param body
 * @returns {Promise}
 */
const http = (url, method, body) => {
  const fullUrl = baseUrl + url;

  return fetch(fullUrl, {
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
      'App-Identity': appIdentity,
      'User-Locale': getLocaleFromCookie()
    },
    method,
    body: JSON.stringify(body)
  })
    .then((res) => {
      const { ok, status } = res;

      return res.json().then((body) => ({ body, ok, status }));
    }).then(({ body, ok, status }) => {
      if (ok) {
        return body;
      } else {
        logApiError(method, status, body.message, fullUrl, body);
        throw body;
      }
    });
};

/**
 * Makes a `fetch` call for authentication, saves tokens to cookies on 200,
 * otherwise throws the HTTP response
 * @param url
 * @param body
 * @returns {Promise}
 */
const loginHttp = (url, body) => {
  return http(url, 'POST', body)
    .then((data) => {
      if (data.access_token) {
        saveTokens(data.access_token, data.refresh_token);

        return data;
      }
      throw data;
    });
};

/**
 * Makes a `fetch` call with Authentication from cookies.
 * If token has expired, will try to refresh it.
 * If there is no token and `dropToLogin` is `true`, or refresh failed, will redirect to `/enter`.
 * Throws error if resulting non-200 is not caused by missing auth
 * @param {string} url
 * @param {string} [method]
 * @param [body]
 * @param {boolean} [dropToLogin]
 * @returns {Promise}
 */
const authHttp = (url, method = 'GET', body, dropToLogin = true) => {
  if (!isLoggedIn() && dropToLogin) {
    console.log('Redirects from authHTTP');
    removeTokens();
    document.location = '/enter';
  }

  return fetch(baseUrl + url, {
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
      'App-Identity': appIdentity,
      Authorization: getAccessToken(),
      'User-Locale': getLocaleFromCookie()
    },
    method,
    body: JSON.stringify(body)
  })
    .then((res) => res.json().then((json) => ({
      res: json,
      ok: res.ok,
      status: res.status
    })))
    .then(({ res, ok, status }) => {
      if (ok) {
        return res;
      } else if (res.code === 7) {
        // means user's account is deleted
        window.location = '/enter/restore';
        throw res;
      } else if (status === 401) { // handling "unauthorized" response
        const refreshToken = getRefreshToken();

        if (refreshToken) { // if there's a refresh token, refreshing
          return http('/v1/account/refresh', 'POST', {
            refresh_token: refreshToken
          })
            .then((data) => {
              // if refresh is successful, retrying the request
              if (data.access_token) {
                saveTokens(data.access_token, data.refresh_token);

                return authHttp(url, method, body);
              }

              if (dropToLogin) {
                document.location = '/enter';
              }
              removeTokens();

              return null;
            }, (err) => {
              console.error(new Error('Token refresh error', { cause: err }));
              if (dropToLogin) {
                document.location = '/enter';
              }
              removeTokens();
            });
        }

        // if there's no refresh token dropping back to login
        if (dropToLogin) {
          document.location = '/enter';
        }
        removeTokens();
      } else {
        throw res;
      }

      return null;
    });
};

export {
  baseUrl,
  appIdentity,
  http,
  loginHttp,
  authHttp
};
